import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { IconCheck, IconMinus } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import { useTabThumb } from '../../hooks/useTabThumb'

export type PlanComparisonColumn = {
  name: string
  action: string
  current?: boolean
  /** La columna del plan recomendado. */
  highlight?: boolean
  /** El botón del plan: la última fila de la tabla ancha y, en estrecho, bajo el nombre. */
  cta?: ReactNode
}

export type PlanComparisonSection = {
  title: string
  rows: Array<{
    feature: string
    values: Array<string | boolean>
  }>
}

/**
 * La comparativa entera, en dos formas que se turnan según el ancho del
 * CONTENEDOR (no de la ventana: la barra lateral se come ancho que la ventana
 * no ve).
 *
 *   · Ancha: una tabla sin desplazamiento lateral, con la cabecera clavada bajo
 *     la barra superior. Es lo que hacen Notion, Figma y Webflow: al bajar por
 *     treinta filas, el nombre del plan de cada columna sigue a la vista.
 *   · Estrecha: una pestaña por plan y una lista de dos columnas. Arrastrar de
 *     lado una tabla de cinco columnas en un teléfono obliga a recordar qué
 *     columna era cuál; elegir el plan y leerlo entero, no.
 *
 * Las dos están siempre en el DOM y el CSS enseña una. Con `display: none` la
 * que no se ve sale también del árbol de accesibilidad, así que no se lee dos
 * veces.
 */
export function PlanComparisonTable({
  title,
  columns,
  sections,
}: {
  /** Sin él, el de siempre. Ya no puede tener un valor por defecto en la firma:
   *  el texto sale del catálogo y eso hay que preguntarlo dentro. */
  title?: string
  columns: PlanComparisonColumn[]
  sections: PlanComparisonSection[]
}) {
  const { t } = useI18n()
  const wideRef = useRef<HTMLDivElement | null>(null)
  const stuck = useStuckHeader(wideRef)
  return (
    <section className="dash-plan-comparison">
      <div className="dash-plan-comparison-head">
        <h2>{title ?? t('plans.table.title')}</h2>
        <p>{t('plans.table.lead')}</p>
      </div>
      <div className={`dash-plan-comparison-wide${stuck ? ' is-stuck' : ''}`} ref={wideRef} aria-label={t('plans.table.aria')}>
        <table>
          <colgroup>
            <col className="dash-plan-comparison-col-feature" />
            {columns.map((column) => (
              <col key={column.name} className={column.highlight ? 'is-highlight' : undefined} />
            ))}
          </colgroup>
          {/* El nombre y el estado actual comparten una sola línea. Es lo que
              queda clavado arriba, así que
              cuanto más baja, menos tabla tapa. */}
          <thead>
            <tr>
              <th scope="col"><span className="dash-plan-comparison-corner">{t('plans.table.feature')}</span></th>
              {columns.map((column) => (
                <th scope="col" key={column.name}>
                  <div className="dash-plan-comparison-plan">
                    <strong>{column.name}</strong>
                    {column.action ? <span className={column.current ? 'is-current' : ''}>{column.action}</span> : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <FragmentSection section={section} columns={columns} key={section.title} />
            ))}
          </tbody>
          {/* Los botones cierran la tabla: quien la recorrió entera ya decidió,
              y es ahí donde tiene que estar la acción. */}
          {columns.some((column) => column.cta !== undefined) ? (
            <tfoot>
              <tr>
                <td />
                {columns.map((column) => (
                  <td key={column.name}>{column.cta ?? null}</td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <NarrowComparison columns={columns} sections={sections} />
    </section>
  )
}

/**
 * Si la cabecera de la tabla está clavada ahora mismo.
 *
 * Sólo entonces lleva sombra. Suelta, en su sitio, es la primera fila de la
 * caja y una sombra la despegaría de ella; clavada, sin sombra, las filas se
 * meten por debajo sin que nada marque el corte.
 *
 * Se mide contra el borde de arriba de lo que se desplaza —el marco del panel,
 * no la ventana—: está clavada mientras el borde de arriba de la tabla haya
 * pasado esa línea y el de abajo todavía no.
 */
function useStuckHeader(ref: RefObject<HTMLDivElement | null>): boolean {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const box = ref.current
    if (!box) return
    const scroller = scrollParentOf(box)
    let frame = 0
    const measure = () => {
      frame = 0
      const edge = scroller ? scroller.getBoundingClientRect().top : 0
      const head = box.querySelector('thead')?.getBoundingClientRect().height ?? 0
      const rect = box.getBoundingClientRect()
      /* El aire entre el borde del marco y la cabecera clavada
         (`--plan-compare-gap`) cuenta como parte de la línea: la cabecera se
         clava ahí, no en el borde. */
      const gap = parseFloat(getComputedStyle(box).getPropertyValue('--plan-compare-gap')) || 0
      setStuck(rect.top < edge + gap && rect.bottom > edge + gap + head)
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure) }
    measure()
    window.addEventListener('scroll', schedule, { passive: true, capture: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, { capture: true })
      window.removeEventListener('resize', schedule)
    }
  }, [ref])
  return stuck
}

/** El antepasado que se desplaza en vertical, o `null` si es la ventana. */
function scrollParentOf(node: HTMLElement): HTMLElement | null {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') return parent
  }
  return null
}

function CellValue({ value }: { value: string | boolean }) {
  const { t } = useI18n()
  if (value === true) return <span className="is-included">{t('plans.table.included')}</span>
  if (value === false) return <IconMinus className="is-excluded" size={15} aria-label={t('plans.table.notIncluded')} />
  return <>{value}</>
}

function FragmentSection({ section, columns }: { section: PlanComparisonSection; columns: PlanComparisonColumn[] }) {
  return (
    <>
      <tr className="dash-plan-comparison-section">
        <th colSpan={columns.length + 1} scope="colgroup">{section.title}</th>
      </tr>
      {section.rows.map((row) => (
        <tr key={row.feature}>
          <th scope="row">{row.feature}</th>
          {row.values.map((value, index) => (
            <td key={`${row.feature}-${index}`}>
              <CellValue value={value} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

/**
 * La forma estrecha: se elige un plan y se lee entero.
 *
 * Arranca en el plan recomendado —o en el contratado, si no hay recomendado—
 * porque es el que la página quiere que se mire primero.
 */
function NarrowComparison({ columns, sections }: { columns: PlanComparisonColumn[]; sections: PlanComparisonSection[] }) {
  const { t } = useI18n()
  const initial = Math.max(0, columns.findIndex((column) => column.highlight || column.current))
  const [active, setActive] = useState(initial)
  const tabsRef = useRef<HTMLDivElement | null>(null)
  useTabThumb(tabsRef, active)
  const column = columns[active]
  return (
    <div className="dash-plan-comparison-narrow">
      <div
        className="dash-booking-service-tabs dash-plan-comparison-tabs"
        ref={tabsRef}
        role="tablist"
        aria-label={t('plans.table.aria')}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
          event.preventDefault()
          const next = (active + (event.key === 'ArrowRight' ? 1 : -1) + columns.length) % columns.length
          setActive(next)
          event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
        }}
      >
        <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
        {columns.map((item, index) => (
          <button
            key={item.name}
            type="button"
            role="tab"
            aria-selected={index === active}
            tabIndex={index === active ? 0 : -1}
            className={index === active ? 'is-active' : ''}
            onClick={() => setActive(index)}
          >
            {item.name}
          </button>
        ))}
      </div>
      {column ? (
        <div className="dash-plan-comparison-narrow-body" role="tabpanel" aria-label={column.name}>
          <div className="dash-plan-comparison-narrow-plan">
            <strong>{column.name}</strong>
            {column.action ? <span className={column.current ? 'is-current' : ''}>{column.action}</span> : null}
            {column.cta ? <div className="dash-plan-comparison-narrow-cta">{column.cta}</div> : null}
          </div>
          {sections.map((section) => (
            <section key={section.title}>
              <h3>{section.title}</h3>
              <dl>
                {section.rows.map((row) => (
                  <div key={row.feature}>
                    <dt>{row.feature}</dt>
                    <dd><CellValue value={row.values[active] ?? false} /></dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  )
}
